import { useEffect, useMemo, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { useProfile, candidateSections, type Certification } from '@/hooks/use-profile'
import { Button } from '@/components/ui/button'
import { MonthYearRangeFields } from '@/components/ui/month-year-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkillChipField } from '@/components/skills/skill-chip-field'
import {
  compareMonthYear,
  isCompleteMonthYear,
  isPartialMonthYear,
  monthYearDraftKey,
  monthYearToIso,
  parseIsoToMonthYear,
  type MonthYearDraft,
} from '@/lib/month-year'
import { appendSkillName, sortedSkillNamesKey } from '@/utils/profile-skill-suggestions'
import { Textarea } from '@/components/ui/textarea'
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'

interface CertificationCanvasProps {
  certification: Certification | null
  onClose: () => void
}

interface CertificationDraft {
  name: string
  issuer: string
  issued?: MonthYearDraft
  expires?: MonthYearDraft
  doesNotExpire: boolean
  credentialId: string
  credentialUrl: string
  description: string
  skills: string[]
}

function buildDraft(certification: Certification | null): CertificationDraft {
  return {
    name: certification?.name ?? '',
    issuer: certification?.issuer ?? '',
    issued: parseIsoToMonthYear(certification?.issued_at),
    expires: parseIsoToMonthYear(certification?.expires_at),
    doesNotExpire: certification ? !certification.expires_at : false,
    credentialId: certification?.credential_id ?? '',
    credentialUrl: certification?.credential_url ?? '',
    description: certification?.description ?? '',
    skills: [...(certification?.related_skills ?? [])],
  }
}

export function CertificationCanvas({ certification, onClose }: CertificationCanvasProps) {
  const {
    profile,
    addCertificationAsync,
    updateCertificationAsync,
    deleteCertificationAsync,
  } = useProfile()
  const candidate = candidateSections(profile)

  const isEditMode = certification !== null && !!certification.id

  const liveCertification = useMemo<Certification | null>(() => {
    if (!isEditMode || !certification?.id) return null
    const match = (candidate.certifications ?? []).find((c) => c.id === certification.id)
    return match ?? certification
  }, [isEditMode, certification, candidate.certifications])

  const initialDraft = useMemo(
    () => buildDraft(liveCertification ?? certification),
    [liveCertification, certification]
  )
  const [draft, setDraft] = useState<CertificationDraft>(initialDraft)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    setDraft(buildDraft(liveCertification ?? certification))
    setValidationError(null)
    setSubmitError(null)
    setConfirmingDelete(false)
  }, [liveCertification, certification])

  const fieldsDirty =
    draft.name !== initialDraft.name ||
    draft.issuer !== initialDraft.issuer ||
    draft.description !== initialDraft.description ||
    draft.credentialId !== initialDraft.credentialId ||
    draft.credentialUrl !== initialDraft.credentialUrl ||
    monthYearDraftKey(draft.issued) !== monthYearDraftKey(initialDraft.issued) ||
    monthYearDraftKey(draft.expires) !== monthYearDraftKey(initialDraft.expires) ||
    draft.doesNotExpire !== initialDraft.doesNotExpire

  const skillsDirty = sortedSkillNamesKey(draft.skills) !== sortedSkillNamesKey(initialDraft.skills)
  const isDirty = isEditMode ? fieldsDirty || skillsDirty : true

  const attemptAddRelatedSkill = (rawName: string) => {
    const result = appendSkillName(draft.skills, rawName)
    if (!result.ok) {
      setValidationError(result.error)
      return
    }
    setDraft((current) => ({ ...current, skills: result.skills }))
    setValidationError(null)
  }

  const removeSkill = (skill: string) => {
    setDraft((current) => ({
      ...current,
      skills: current.skills.filter((value) => value !== skill),
    }))
    setValidationError(null)
  }

  const handleSave = async () => {
    setValidationError(null)
    setSubmitError(null)

    if (!draft.name.trim()) {
      setValidationError('Name is required.')
      return
    }
    if (!draft.issuer.trim()) {
      setValidationError('Issuer is required.')
      return
    }
    if (isPartialMonthYear(draft.issued)) {
      setValidationError('Enter both issued month and year, or leave issued empty.')
      return
    }
    if (!draft.doesNotExpire && !isCompleteMonthYear(draft.expires)) {
      setValidationError(
        'Expiry month and year are required, or mark that this credential does not expire.',
      )
      return
    }
    if (
      !draft.doesNotExpire &&
      isCompleteMonthYear(draft.issued) &&
      isCompleteMonthYear(draft.expires) &&
      compareMonthYear(draft.expires, draft.issued) < 0
    ) {
      setValidationError('Expiry cannot be before issue date.')
      return
    }

    const issuedIso = isCompleteMonthYear(draft.issued) ? monthYearToIso(draft.issued) : null
    const expiresIso =
      draft.doesNotExpire || !isCompleteMonthYear(draft.expires)
        ? null
        : monthYearToIso(draft.expires)

    setIsSaving(true)
    try {
      if (!isEditMode) {
        await addCertificationAsync({
          name: draft.name.trim(),
          issuer: draft.issuer.trim(),
          issued_at: issuedIso,
          expires_at: expiresIso,
          credential_id: draft.credentialId.trim() || undefined,
          credential_url: draft.credentialUrl.trim() || undefined,
          description: draft.description.trim() || undefined,
          related_skills: draft.skills,
        })
      } else if (liveCertification?.id) {
        const updates: Partial<Omit<Certification, 'id'>> = {}
        if (fieldsDirty) {
          updates.name = draft.name.trim()
          updates.issuer = draft.issuer.trim()
          updates.issued_at = issuedIso
          updates.expires_at = expiresIso
          updates.credential_id = draft.credentialId.trim() || undefined
          updates.credential_url = draft.credentialUrl.trim() || undefined
          updates.description = draft.description.trim() || undefined
        }
        if (skillsDirty) {
          updates.related_skills = draft.skills
        }

        if (Object.keys(updates).length > 0) {
          await updateCertificationAsync({
            id: liveCertification.id,
            updates,
          })
        }
      }

      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save certification.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!isEditMode || !liveCertification?.id) return

    if (!confirmingDelete) {
      setConfirmingDelete(true)
      return
    }

    setSubmitError(null)
    setIsDeleting(true)
    try {
      await deleteCertificationAsync(liveCertification.id)
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to delete certification.')
      setIsDeleting(false)
      setConfirmingDelete(false)
    }
  }

  const isBusy = isSaving || isDeleting

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="artifact-cert-name">Name *</Label>
            <Input
              id="artifact-cert-name"
              value={draft.name}
              onChange={(event) => {
                setDraft((current) => ({ ...current, name: event.target.value }))
                setValidationError(null)
              }}
              placeholder="Certification name"
              disabled={isBusy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="artifact-cert-issuer">Issuer *</Label>
            <Input
              id="artifact-cert-issuer"
              value={draft.issuer}
              onChange={(event) => {
                setDraft((current) => ({ ...current, issuer: event.target.value }))
                setValidationError(null)
              }}
              placeholder="AWS, Microsoft, etc."
              disabled={isBusy}
            />
          </div>
        </div>

        <MonthYearRangeFields
          idPrefix="artifact-cert"
          ongoingLabel="Does not expire"
          ongoingChecked={draft.doesNotExpire}
          onOngoingChange={(checked) => {
            setDraft((current) => ({
              ...current,
              doesNotExpire: checked,
              expires: checked ? undefined : current.expires,
            }))
            setValidationError(null)
          }}
          start={draft.issued}
          end={draft.expires}
          onStartChange={(issued) => {
            setDraft((current) => ({ ...current, issued }))
            setValidationError(null)
          }}
          onEndChange={(expires) => {
            setDraft((current) => ({ ...current, expires }))
            setValidationError(null)
          }}
          startMonthLabel="Issued month"
          startYearLabel="Issued year"
          endMonthLabel="Expiry month"
          endYearLabel="Expiry year"
          endAllowsFutureYears
          disabled={isBusy}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="artifact-cert-credential-id">Credential ID</Label>
            <Input
              id="artifact-cert-credential-id"
              value={draft.credentialId}
              onChange={(event) => {
                setDraft((current) => ({ ...current, credentialId: event.target.value }))
              }}
              placeholder="Optional ID or license number"
              disabled={isBusy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="artifact-cert-credential-url">Credential URL</Label>
            <Input
              id="artifact-cert-credential-url"
              value={draft.credentialUrl}
              onChange={(event) => {
                setDraft((current) => ({ ...current, credentialUrl: event.target.value }))
              }}
              placeholder="https://..."
              disabled={isBusy}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-cert-description">Description</Label>
          <Textarea
            id="artifact-cert-description"
            rows={4}
            value={draft.description}
            onChange={(event) => {
              setDraft((current) => ({ ...current, description: event.target.value }))
            }}
            placeholder="Notes about this credential..."
            disabled={isBusy}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-cert-skills-input">Related skills</Label>
          <SkillChipField
            id="artifact-cert-skills-input"
            skills={draft.skills}
            profileSkills={candidate.skills}
            disabled={isBusy}
            onAdd={attemptAddRelatedSkill}
            onRemove={removeSkill}
            placeholder="Add a skill covered by this credential…"
            helperText="Pick from your profile skills or create a new one — it will be added to your profile when you save."
          />
        </div>
      </form>

      <ArtifactCanvasInlineError>{validationError || submitError}</ArtifactCanvasInlineError>

      <ArtifactCanvasFooterBar
        leading={
          isEditMode ? (
            <Button
              type="button"
              variant={confirmingDelete ? 'destructive' : 'ghost'}
              onClick={handleDelete}
              disabled={isBusy}
              className={confirmingDelete ? '' : 'text-destructive hover:text-destructive'}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {isDeleting ? 'Deleting...' : confirmingDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          ) : undefined
        }
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isSaving}
            saveLabel={isEditMode ? 'Save' : 'Add certification'}
            savingLabel="Saving..."
            closeDisabled={isBusy}
            saveDisabled={!isDirty || isBusy}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
